import * as _ from 'lodash'
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import globalRelay from './globalRelay'; // be sure this is imported in the build before other app code
import componentServices from './componentServices';
import router from './router';
import { Transition, TransitionGroup } from 'react-transition-group';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import store, { DomainEntityType, getDomainEntities } from './store';
import * as actions from './actions'; // this needs to be imported before extensions to be sure all core globalRelay handlers are registered first
import * as employeeActions from './employeeActions';

// auto-registered services
import './services/securityContext';
import './services/operationsHelper';

import { Alert } from './components/sharedComponents';
import ForgotPassword from './components/forgotPassword';
import Login from './components/login';
import ResetPassword from './components/resetPassword';

// not used but need for build discovery
import AdminModule from './components/adminModule';
import BuilderModule from './components/builderModule';
import JobEdit from './components/jobEdit';
import './components/fieldInspectionModule';
import './components/operationsModule';
import OrderDeskModule from './components/orderDeskModule';
import RetailModule from './components/retailModule';
import ScanManagementModule from './components/scanManagementModule';
import FieldAppProvider from './contexts/fieldAppProvider';
import SystemUtil from './components/systemUtil';
import * as extensions from './extensions'; // imported last to be sure that extensions and overrides are applied after base code
import './_templates'; // here to expose templates when running in a development environment
import { IAlert } from './models/alert';
import { IModal } from './models/modal';
import { Map } from 'immutable';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const imports = {
	AdminModule,
	BuilderModule,
	JobEdit,
	OrderDeskModule,
	RetailModule,
	ScanManagementModule,
	SystemUtil,
	extensions
};

class Header extends React.Component<Record<string, any>> {
	constructor(props) {
		super(props);

		this.onToggleSidebar = this.onToggleSidebar.bind(this);
	}

	shouldComponentUpdate(nextProps) {
		return nextProps.store !== this.props.store;
	}

	handleModuleClick(moduleKey) {
		router.pushHistory(`/${moduleKey}`);
	}

	onToggleSidebar(e) {
		e.preventDefault();
		actions.toggleSidebar();
	}

	render() {
		const { route, store } = this.props;
		// const { store } = componentServices;
		const currentEmployee = componentServices.securityContext.getCurrentEmployee();
		const currentUser = componentServices.securityContext.getCurrentUser();
		const headerActions = store.getIn(['viewState', 'headerActions']);
		const moduleKey = router.getPathSegments()[0];
		const currentModule = _.find(getDomainEntities({ store, type: DomainEntityType.modules }), { key: moduleKey });
		const currentModuleName = currentModule ? currentModule.name : '';

		const userName = (currentEmployee) ? [currentEmployee.firstName, currentEmployee.lastName].join(' ') : (currentUser) ? currentUser.userName : ''; // eslint-disable-line no-nested-ternary
		const avatarUrl = (currentEmployee) ? currentEmployee.avatarUrl : undefined;
		const userPhotoStyle = avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined;

		const actionComponents: JSX.Element[] = [];

		headerActions.forEach((v, k) => {
			// TODO: ensure that v component is re-rendered when store changes
			actionComponents.push(<div key={k}>{ React.cloneElement(v, { store }) }</div>);
		});

		return (
			<header>
				<button className="menu" onClick={this.onToggleSidebar}><span /><span /><span /></button>
				<a className="userInfo" onClick={this.handleModuleClick.bind(this, moduleKey)}><span className={`userPhoto ${moduleKey}`} style={userPhotoStyle} /><span className="userName">{userName}</span><span className="userModule">{currentModuleName}</span></a>
				<div className="actions">{actionComponents}</div>
				{route.heading || <div className="logo" />}
			</header>
		);
	}
}

class Sidebar extends React.Component<any, any> {
	constructor(props) {
		super(props);

		this.handleLogoutClick = this.handleLogoutClick.bind(this);
		this.handleUtilClick = this.handleUtilClick.bind(this);
	}

	handleLogoutClick(e) {
		e.preventDefault();
		actions.logout();
	}

	handleRouteClick(pathName, e) {
		e.preventDefault();
		actions.closeSidebar();
		router.pushHistory(pathName);
	}

	handleUtilClick(e) {
		e.preventDefault();

		actions.pushModal({
			component: <SystemUtil />
		});
	}

	render() {
		const items: JSX.Element[] = [];
		const user = componentServices.securityContext.getCurrentUser();
		const employee = componentServices.securityContext.getCurrentEmployee();
		const modules = getDomainEntities({ store: componentServices.store, type: DomainEntityType.modules });

		if (user) {
			_.forEach(modules, x => {
				if (user.hasModule(x.key)) {
					const path = '/' + x.key;
					const classNames = [_.camelCase(x.key)];

					if (router.isActive(path)) {
						classNames.push('on');
					}

					items.push(<li onClick={this.handleRouteClick.bind(this, path)} className={classNames.join(' ')} key={x.key}><span>{x.name}</span></li>);
				}
			});
		}
		else {
			items.push(<li onClick={this.handleRouteClick.bind(this, '/login')} key={'login'}><span>Log In</span></li>);
		}
		return (
			<div className="sidebar">
				<header>Relay</header>
				<ul className="menu">
					{items}
				</ul>
				<div className="actions">
					{ employee? <button onClick={this.handleRouteClick.bind(this, `/system-admin/employee/${employee?.key}`) }><span>Edit Profile</span></button>: null }
					<div className="version">Relay Version {globalRelay.version}<a onClick={this.handleLogoutClick}><span>Log Out</span></a></div>
					{user && user.userName == 'darylridgely' ? <button onClick={this.handleUtilClick}><span>Util</span></button> : null}
				</div>
			</div>
		);
	}
}

class Modal extends React.PureComponent<Record<string, any>> {
	private _shade;

	constructor(props) {
		super(props);

		this.handleClick = this.handleClick.bind(this);
		this._shade = React.createRef();
	}

	handleClick(e) {
		if (!this.props.preventShadeHide && this._shade && e.target === this._shade.current) {
			e.preventDefault();
			actions.popModal();
		}
	}

	render() {
		const { children, ...props } = this.props;

		return (<Transition timeout={{ enter: 10, exit: props.transitionExit || 210 }} appear={true} {...props}>{state => {
			let { classNames = [] } = props;
			const { inClass = 'in', outClass = 'out', zIndex } = props;

			classNames = _.filter(classNames, c => ['modal', inClass, outClass].indexOf(c) === -1);
			classNames.unshift('modal');

			// console.log(state);

			if (['entered'].indexOf(state) !== -1) {
				classNames.push(inClass);
			}
			else {
				classNames.push(outClass);
			}

			return (<div className={classNames.join(' ')} style={{ zIndex }} onClick={this.handleClick} ref={this._shade}>
				{children && React.cloneElement(children, { store: this.props.store, isModal: true })}
			</div>);
		}}</Transition>);
	}
}

class AppInternal extends React.Component<any, any> {
	private _lastScrollContentKey;
	private _scrollDiv;

	constructor(props) {
		super(props);

		this.handleWindowResize = this.handleWindowResize.bind(this);
	}

	bindWindowSize() {
		const windowSize = store().getIn(['viewState', 'metrics', 'windowSize']);

		windowSize.withMutations(ws => {
			const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
			const h = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

			ws.set('width', w).set('height', h);
		});
	}

	handleWindowResize() {
		this.bindWindowSize();
	}

	componentDidMount() {
		this.bindWindowSize();
		window.addEventListener('resize', this.handleWindowResize);
		// TransitionGroup makes it nearly impossible to reliably use refs
		this._scrollDiv = document.querySelector('#appRoot > .content');
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.handleWindowResize);
	}

	render() {
		const viewState = componentServices.store.get('viewState');
		const sidebarClass = (viewState.getIn(['sidebar', 'expanded'])) ? 'sidebarExpanded' : 'sidebarCollapsed';
		const headerDefs = viewState.get('headers');
		const modalDefs = viewState.get('modals') as IModal[];
		const banner = viewState.get('banner');
		const tooltip = viewState.get('tooltip');
		const alertDefs = viewState.get('alerts') as IAlert[];
		const scrollContentKey = viewState.get('scrollContentKey');
		const PrintChild = viewState.get('printChildren');
		const routeState = viewState.get('routeState');
		const route = componentServices.route;
		const contentClass = ['content scrollable', route.contentClass].join(' ');
		const currentUser = componentServices.securityContext.getCurrentUser();
		const subHeaders: JSX.Element[] = [];
		const modals: JSX.Element[] = [];
		const alerts: JSX.Element[] = [];
		let alertRenderer:IModal;
		const Child = route.component;
		const childElement = route.element;
		let modalCount = 0;

		this._lastScrollContentKey = this._lastScrollContentKey || scrollContentKey;

		if (this._lastScrollContentKey !== scrollContentKey && this._scrollDiv) {
			this._scrollDiv.scrollTop = 0;
			this._lastScrollContentKey = scrollContentKey;
		}

		modalDefs.forEach(function (def) {
			// modals have the store set as a prop to be sure that any child components update if they depend on store changes
			modals.push(
				<Modal key={def.key || ('modal-' + modalCount)}
					preventShadeHide={def.preventShadeHide}
					inClass={def.inClass}
					outClass={def.outClass}
					store={componentServices.store}
					zIndex={100 + modalCount}
					{...def.props}>{def.component}</Modal>);
			modalCount++;
			if(def.rendersAlerts) {
				alertRenderer = def;
			}
		});

		if (tooltip) {
			modals.push(
				<Modal key={tooltip.key || ('modal-' + modalCount)}
					preventShadeHide={tooltip.preventShadeHide}
					inClass={tooltip.inClass}
					outClass={tooltip.outClass}
					store={componentServices.store}
					zIndex={100 + modalCount}
					{...tooltip.props}>{tooltip.component}</Modal>);
		}

		if(!alertRenderer) {
			alertDefs.forEach(function (def) {
				alerts.push(<Alert type={def.type} message={def.message} isCloseable={def.isCloseable} clear={def.clear} key={def.key} />);
			});
		}

		headerDefs.forEach(function (def) {
			subHeaders.push(React.cloneElement(def, { store: componentServices.store, routeState }));
		});

		const mainHeader = (currentUser) ? <Header store={componentServices.store} route={route} /> : undefined;
		const RootComponent = globalRelay.clientMode === 'FieldApp'? FieldAppProvider: React.Fragment;
		return (
			<RootComponent>
				{PrintChild ? <div id="printRoot"><PrintChild /></div> : null}
				<div id="appRoot" className={sidebarClass} >
					{mainHeader}
					{subHeaders}
					<div className="alerts">
						{alerts}
					</div>
					<div id="pinnedStats" className="modal pinned in" />
					{ banner? <div className={ `banner ${banner.type}`}>{ banner.message }</div>: null }
					
					<div className={contentClass}>
						{ Child? <Child store={componentServices.store} routeState={ routeState } />: null }
						{ childElement? React.cloneElement(childElement, { store: componentServices.store, routeState }): null }

					</div>
					<Sidebar />
					<footer />
					<TransitionGroup component={null}>
						{modals}
					</TransitionGroup>
				</div>
			</RootComponent>
		);
	}
}

let appRoot:Root;
function render() {
	// snapshot the store so that rendering and access are predictable through the render lifecycle
	componentServices.set('store', store());
	// see https://reactjs.org/docs/strict-mode.html#detecting-unexpected-side-effects
	appRoot.render(<React.StrictMode><DndProvider backend={HTML5Backend}><AppInternal /></DndProvider></React.StrictMode>);
	//appRoot.render(<DndProvider backend={HTML5Backend}><AppInternal /></DndProvider>);
}

router.addRoutes([{
	path: '/',
	component: () => <div />,
	onEnter: router.requireUser()
}, {
	path: '/login',
	component: Login,
	contentClass: 'login'
}, {
	path: '/forgot-password',
	component: ForgotPassword,
	contentClass: 'login'
}, {
	path: '/reset-password/:token',
	component: ResetPassword,
	contentClass: 'login'
}]);

const _moduleTest = /\/([^\/]+)/;
router.onRouteChange(({ current, previous }) => {
	const currentModuleTest = _moduleTest.exec(current.location.pathname);
	const currentModule = currentModuleTest && currentModuleTest[1];
	const previousModuleTest = previous && _moduleTest.exec(previous.location.pathname);
	const previousModule = previousModuleTest && previousModuleTest[1];

	const storeCursor = store();

	storeCursor.withMutations(mutable => {
		mutable.setIn(['viewState', 'rootPath'], current.location.pathname);
		mutable.setIn(['viewState', 'routeState'], current.location.state);
		mutable.setIn(['viewState', 'currentModule'], currentModule);

		if (previousModule && previousModule !== currentModule) {
			mutable.setIn(['model', 'jobs'], mutable.getIn(['model', 'jobs']).clear());
			mutable.setIn(['model', 'jobCosts'], mutable.getIn(['model', 'jobCosts']).clear());
		}

		mutable.getIn(['viewState', 'alerts']).clear();
		mutable.setIn(['viewState', 'pinnedStats'], Map());
		mutable.setIn(['viewState', 'scrollContentKey'], { key: current.location.pathname });
	});
});

// bootstrap is a special case - in general do not use promise from actions
actions.bootstrap()
	.then(result => { // eslint-disable-line no-unused-vars
		let employeesFetched = false;

		store.listen(function () {
			if (!employeesFetched && store().getIn(['viewState', 'currentUser'])) {
				// these are needed in so many places, let's just get them right away
				employeesFetched = true;
				employeeActions.fetchEmployees();
			}
			if(!appRoot) {
				const appWrapper:HTMLElement = document.getElementById('appWrapper');
				appRoot = createRoot(appWrapper!);
			}
			render();
		});

		globalRelay.triggerAppBootstrapped({ authenticated: !!result.currentUser });
		router.setLocation(router.history.location, router.history.action);
	});